// @ts-nocheck
import { useTranslation } from "react-i18next";
import { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createRoot } from "react-dom/client";
import DropDownListComponent from "@CubeComponents/form/DropdownListComponent";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import UsersIcon from "../../../assets/icons/svg/usersIcon.svg?react";
import AddIcon from "../../../assets/icons/Create.svg?react";
import Maximize from "../../../assets/icons/Maximize.svg";
import RichTextEditorComponent from "../../../cubeComponent/form/RichTextEditorComponent";
import { HtmlEditor, Inject, Toolbar, Count } from "@syncfusion/ej2-react-richtexteditor";
import FileManagerAsPopup from "../../../components/FileManager/FileManagerAsPopup";
import FormPreview from "../../../components/PortalSetings/Configuration/FormsAndFields/FormTemplate/formPreview";
import { formCategories } from "@Helpers";
import { Accordion, AccordionDetails, AccordionSummary, CircularProgress } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SpaceUserSelector from "./SpaceUserSelector";
import SlickGrid from "../../../cubeComponent/html/SlickGrid";
import UserNameTagComponent from "../../../cubeComponent/html/UserNameTagComponent";
import DeleteIcon from "@Components/StickySidebarViewer/assets/DeleteIcon";
import CreateIcon from "@Components/StickySidebarViewer/assets/CreateIcon";
import NoDataSvg from "./../../../cubeComponent/html/NoData";
import { spacesDataService } from "../../../services/dataManager";
import { Query } from "@syncfusion/ej2-data";
import { showToastNotification } from "../../../actions/toast";
import {
  formatBytes,
  SOMETHING_WENT_WRONG,
  TOAST_TYPE_ERROR,
  TOAST_TYPE_SUCCESS,
  TOAST_TYPE_WARNING,
} from "../../../services/constants";
import { convertDate } from "../../../services/luxonHelper";
import { getFormTemplate } from "../../../actions/fileUpload";
import { saveTransmitalsData, setTransmittalDataForReviews } from "../../../actions/transmitals";
import useFormValidator from "../../../components/FormValidation/useFormValidator";
import { getFileImage } from "../../../services/utils/iconMap";
import IconComponent from "@CubeComponents/icons";

interface Space {
  _id: string;
  description: string;
  spaceName: string;
  createdDate: number;
  status: string;
  id: string;
  createdBy: string;
  spaceId: string;
  projectId: string;
  createdByName: string;
}

type OriginSpace = Pick<Space, "_id" | "spaceName">;

interface CreateTransmittalProps {
  selectedSpaceId?: string | null;
  selectedReferences?: any | null;
  reviewId?: string | null;
  projectId: string;
  isOpen: boolean;
  onClose: () => void;
}

interface SelectedUsers {
  id: string;
  spaceId: string;
  spaceName: string;
}

interface ReferencesDataset {
  name: string;
  versionNumber: number;
  size: number;
  dateCreated: number;
}

const RemarksCell = ({ id, initialValue, onValueChange }) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState(initialValue || "");

  const handleChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
  };

  const handleBlur = () => {
    onValueChange(id, inputValue);
  };

  return (
    <div className="px-4 py-2 w-full">
      <input
        type="text"
        value={inputValue}
        placeholder={t("Enter Remarks")}
        className="w-full border border-system_colour_45 rounded-md p-2 text-system_colour_45"
        onChange={handleChange}
        onBlur={handleBlur}
      />
    </div>
  );
};

function CreateTransmittal({
  selectedSpaceId = null,
  selectedReferences = null,
  reviewId = null,
  projectId,
  isOpen,
  onClose,
}: CreateTransmittalProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const formValidator = useFormValidator({});
  const originSpaceRef = useRef<any>(null);
  const referencesRef = useRef<any>(null);
  const richTextEditorRef = useRef<any>(null);

  // Component States
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isMaximized, setIsMaximized] = useState<boolean>(false);
  const [originSpace, setOriginSpace] = useState<OriginSpace | null>(null);
  const [enableOriginSpace, setEnableOriginSpace] = useState<boolean>(true);
  const [spaces, setSpaces] = useState<Space[] | null>(null);
  const [transmittalSubject, setTransmittalSubject] = useState<string>("");
  const [transmittalRemarks, setTransmittalRemarks] = useState<string>("");
  const [filteredSpaceList, setFilteredSpaceList] = useState<Space[]>([]);
  const [selectedToUsers, setSelectedToUsers] = useState<SelectedUsers[] | []>([]);
  const [selectedNotifyUsers, setSelectedNotifyUsers] = useState<SelectedUsers[] | []>([]);
  const [toSpaceEnable, setToSpaceEnable] = useState<boolean>(true);
  const [notifySpaceEnable, setNotifySpaceEnable] = useState<boolean>(true);
  const [isToSelectorOpen, setIsToSelectorOpen] = useState<boolean>(false);
  const [isNotifySelectorOpen, setIsNotifySelectorOpen] = useState<boolean>(false);
  const [isReferencesManagerOpen, setIsReferencesManagerOpen] = useState<boolean>(false);
  const [transmittalReferences, setTransmittalReferences] = useState<{ [key: string]: any }>({});
  const [referencesDataset, setReferencesDataset] = useState<ReferencesDataset[]>([]);
  const [alreadyAttachedResources, setAlreadyAttachedResources] = useState<string[]>([]);
  const [formTemplate, setFormTemplate] = useState<any>(null);
  const [selectedRowCount, setSelectedRowCount] = useState<number>(0);
  const [enableSendTransmittal, setEnableSendTransmittal] = useState<boolean>(false);
  const [buttonLoading, setButtonLoading] = useState<boolean>(false);

  // Component Redux Data
  const projectData = useSelector((state: any) => state.projectData);
  const originSpaceQuery = new Query().addParams("projectId", projectId);

  // Toolbar Settings for Rich Text Editor
  const editorToolbarSettings = {
    items: [
      "Undo",
      "Redo",
      "|",
      "FontName",
      "FontSize",
      "|",
      "cut",
      "copy",
      "paste",
      "|",
      "Bold",
      "Italic",
      "Underline",
      "StrikeThrough",
    ],
  };

  // useEffect to load all spaces in a project and set the default one for the dropdown - handle case where selectedSpaceId is passed as a prop - disable origin dropdown
  useEffect(() => {
    let isMounted = true;
    const loadSpaces = async () => {
      setIsLoading(true);
      try {
        const result = await spacesDataService.executeQuery(originSpaceQuery);

        if (isMounted) {
          setSpaces(result.result || []);

          if (selectedSpaceId) {
            const selectedSpace = result.result.find((space) => space._id === selectedSpaceId);
            if (selectedSpace) {
              setOriginSpace({
                _id: selectedSpace._id,
                spaceName: selectedSpace.spaceName,
              });
              setEnableOriginSpace(false);
            }
          } else if (result.result && result.result.length > 0) {
            setOriginSpace({
              _id: result.result[0]._id,
              spaceName: result.result[0].spaceName,
            });
          }
        }
      } catch (error) {
        console.error("Error loading spaces:", error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    if (isOpen) {
      loadSpaces();
      if (selectedReferences?.length) {
        setIsLoading(true);
        const referencesData = [];
        const addedResources = [];
        const referencesObj = {};
        selectedReferences.forEach((reference) => {
          if (reference.fileVersion) {
            addedResources.push(reference.fileVersion);

            referencesData.push({
              id: reference.fileVersion,
              name: reference.name,
              versionNumber: reference.versionNumber,
              size: reference.size,
              dateCreated: reference.dateCreated,
              resourceType: reference.resourceType,
              fileType: reference.fileType,
              remarks: reference.remarks || "",
            });

            referencesObj[reference.fileVersion] = {
              fileVersion: reference.fileVersion,
              name: reference.name,
              versionNumber: reference.versionNumber,
              size: reference.size,
              dateCreated: reference.dateCreated,
              resourceType: reference.resourceType,
              fileType: reference.fileType,
              remarks: reference.remarks || "",
              ...(reference.transmittalVersionId && {
                transmittalVersionId: reference.transmittalVersionId,
              }),
            };
          }
        });

        setAlreadyAttachedResources(addedResources);
        setReferencesDataset(referencesData);
        setTransmittalReferences(referencesObj);
        setIsLoading(false);
      }
    }

    return () => {
      clearFormData();
      isMounted = false;
    };
  }, [isOpen, projectId, selectedSpaceId]);

  // useEffect to fetch the Transmittal Custom Fields form template
  useEffect(() => {
    getFormTemplateData();
  }, [projectData?.id]);

  useEffect(() => {
    return () => {
      if (richTextEditorRef.current) {
        richTextEditorRef.current.destroy();
      }
    };
  }, [isOpen]);

  // useEffect to set filtered spaces for the SpaceUserSelector Component and disable/enable To/Notify button if origin space is not selected
  useEffect(() => {
    if (originSpace?._id) {
      setToSpaceEnable(true);
      setNotifySpaceEnable(true);
      const filteredSpaces = spaces.filter((space) => space._id !== originSpace?._id);
      setFilteredSpaceList(filteredSpaces);
    } else {
      setToSpaceEnable(false);
      setNotifySpaceEnable(false);
    }
  }, [originSpace]);

  // useEffect to enable/disable the Send Transmittal button
  useEffect(() => {
    if (transmittalSubject.trim().length && Object.keys(transmittalReferences).length != 0) {
      setEnableSendTransmittal(true);
    } else {
      setEnableSendTransmittal(false);
    }
  }, [transmittalSubject, transmittalReferences, selectedToUsers]);

  // Function to fetch custom fields form template for Transmittals
  const getFormTemplateData = async () => {
    if (!projectData?.id) return;
    const response = await getFormTemplate({
      projectId: projectData.id,
      resourceType: formCategories.transmittal,
    });
    if (response.data.status && response.data.data) {
      setFormTemplate(response.data.data);
    }
  };

  // Handler function to change origin space from the Origin Space Dropdown
  const changeOriginSpace = (event) => {
    if (event.value) {
      clearFormData();
      const selectedSpace = spaces.find((space) => space._id === event.value);
      if (selectedSpace) {
        setOriginSpace({
          _id: selectedSpace._id,
          spaceName: selectedSpace.spaceName,
        });
      }
    }
  };

  // Origin Space Dropdown component
  const OriginSpaceDropDownList = useCallback(
    () => (
      <DropDownListComponent
        ref={originSpaceRef}
        dataSource={spaces}
        fields={{ text: "spaceName", value: "_id" }}
        onChange={changeOriginSpace}
        enabled={enableOriginSpace}
        value={originSpace?._id || ""}
        allowFiltering
      />
    ),
    [spaces, enableOriginSpace, originSpace?._id],
  );

  // Handler functions

  const toolbarClickHandler = (args) => {
    if (args == "add") {
      handleReferencesPopup();
    } else if (args == "delete") {
      handleReferencesDelete();
    }
  };

  const updateRemark = (id, value) => {
    setTransmittalReferences((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        remarks: value,
      },
    }));
  };

  const handleSpaceUsers = (data) => {
    const result = {};
    data.forEach((item) => {
      const spaceUserId = item.id.split("_")[0];
      const spaceId = item.spaceId;
      if (!result[spaceId]) {
        result[spaceId] = {
          spaceId: spaceId,
          userId: [],
        };
      }
      if (!result[spaceId].userId.includes(spaceUserId)) {
        result[spaceId].userId.push(spaceUserId);
      }
    });
    return Object.values(result);
  };

  const handleSendTransmittal = async () => {
    const res = formValidator.validateAll();
    if (!res) {
      dispatch(
        showToastNotification({
          content: t("Please check custom fields"),
          type: TOAST_TYPE_ERROR,
        }),
      );
      return;
    }

    const transmittalAttachedReferences = [];
    const forwardedReferences = [];
    const toSpaceIds = handleSpaceUsers(selectedToUsers);
    const ccSpaceIds = handleSpaceUsers(selectedNotifyUsers);

    if (selectedToUsers.length === 0) {
      dispatch(
        showToastNotification({
          content: t("Please select at least one To user"),
          type: TOAST_TYPE_ERROR,
        }),
      );
      return;
    }
    Object.keys(transmittalReferences).forEach((reference) => {
      let resourceId;
      if (["RESOURCE_TYPE_FILE", "RESOURCE_TYPE_MERGED_FILE"].includes(transmittalReferences[reference].resourceType)) {
        resourceId = transmittalReferences[reference].fileVersion;
        transmittalAttachedReferences.push({
          fileVersionId: resourceId,
          remarks: transmittalReferences[reference].remarks || "",
        });
      }

      if (transmittalReferences[reference].transmittalVersionId) {
        forwardedReferences.push({
          resourceType: transmittalReferences[reference].resourceType,
          resourceId,
          transmittalId: transmittalReferences[reference].transmittalVersionId,
          remarks: transmittalReferences[reference].remarks || "",
        });
      }
    });

    const transmittalData = {
      subject: transmittalSubject.trim(),
      remarks: transmittalRemarks,
      spaceId: originSpace?._id,
      toSpaceIds,
      ccSpaceIds,
      attachedResources: transmittalAttachedReferences,
      forwardedResources: forwardedReferences,
    };

    if (formTemplate) {
      transmittalData.formTemplate = {
        _id: formTemplate._id,
        template: formTemplate.template,
      };
    }

    if (reviewId) {
      transmittalData.reviewData = { reviewId };
    }
    setButtonLoading(true);
    const response = await saveTransmitalsData(transmittalData);
    if (response.data.status) {
      dispatch(
        showToastNotification({
          content: `TS-${response.data.data.counter} has been sent successfully!`,
          type: TOAST_TYPE_SUCCESS,
        }),
      );
      if (reviewId) {
        dispatch(setTransmittalDataForReviews(response.data.status));
      }
    } else {
      dispatch(
        showToastNotification({
          content: SOMETHING_WENT_WRONG,
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
    setButtonLoading(false);
    clearFormData();
    onClose();
  };

  const handleToClick = () => {
    setIsToSelectorOpen(true);
  };

  const handleNotifyClick = () => {
    setIsNotifySelectorOpen(true);
  };

  const handleReferencesPopup = () => {
    if (!originSpace?._id) {
      dispatch(showToastNotification("Please select origin space", TOAST_TYPE_WARNING));
      return;
    }
    setIsReferencesManagerOpen(!isReferencesManagerOpen);
  };

  const handleReferencesDelete = () => {
    if (referencesRef.current && referencesRef.current.slickGrid) {
      const selectedRows = referencesRef.current.slickGrid.getSelectedRows();
      if (selectedRows.length === 0) {
        dispatch(
          showToastNotification({
            content: t("Please select at least one reference to delete"),
            type: TOAST_TYPE_ERROR,
          }),
        );
        return;
      }

      const selectedItems = selectedRows.map((rowIdx) => referencesRef.current.dataView.getItem(rowIdx));

      const updatedReferencesDataset = referencesDataset.filter(
        (item) => !selectedItems.some((selectedItem) => selectedItem.id === item.id),
      );

      const updatedAttachedResources = alreadyAttachedResources.filter(
        (fileId) => !selectedItems.some((selectedItem) => selectedItem.id === fileId),
      );

      const updatedTransmittalReferences = { ...transmittalReferences };
      selectedItems.forEach((item) => {
        delete updatedTransmittalReferences[item.id];
      });

      setReferencesDataset(updatedReferencesDataset);
      setAlreadyAttachedResources(updatedAttachedResources);
      setTransmittalReferences(updatedTransmittalReferences);

      referencesRef.current.slickGrid.setSelectedRows([]);

      dispatch(
        showToastNotification({
          content: `${selectedRows.length} reference(s) removed successfully`,
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    }
  };

  const handleReferencesPopupCancelClick = () => {
    setIsReferencesManagerOpen(false);
  };

  const handleReferencesSelectedButtonClick = async (data) => {
    const attachedReferences = { ...transmittalReferences };

    Object.keys(data).forEach((key) => {
      attachedReferences[key] = {
        ...data[key],
        remarks: attachedReferences[key]?.remarks || "",
      };
    });

    const allSelectedReferences = [];
    const referencesData = [];

    Object.keys(attachedReferences).forEach((reference) => {
      allSelectedReferences.push(attachedReferences[reference].fileVersion);
      referencesData.push({
        id: attachedReferences[reference]?.fileVersion,
        name: attachedReferences[reference]?.name,
        versionNumber: attachedReferences[reference]?.versionNumber,
        size: attachedReferences[reference]?.size,
        dateCreated: attachedReferences[reference]?.dateCreated,
        resourceType: attachedReferences[reference]?.resourceType,
        fileType: attachedReferences[reference]?.fileType,
        remarks: attachedReferences[reference]?.remarks || "",
      });
    });
    setReferencesDataset(referencesData);
    setAlreadyAttachedResources(allSelectedReferences);
    setTransmittalReferences(attachedReferences);
    setIsReferencesManagerOpen(false);
  };

  const handleReferencesGridClick = () => {};

  const handleSelectionChanged = () => {
    if (referencesRef.current && referencesRef.current.slickGrid) {
      const selectedRows = referencesRef.current.slickGrid.getSelectedRows();
      setSelectedRowCount(selectedRows.length);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleMaximize = () => {
    setIsMaximized((prev) => !prev);
  };

  const handleTransmittalRemarksChange = (args) => {
    setTransmittalRemarks(args.value);
  };

  const handleCustomFieldsData = (data) => {
    setFormTemplate((prev) => ({ ...prev, template: data }));
  };

  const handleSpaceUserSelection = (users: SelectedUsers, type: string) => {
    if (type === "To") {
      setSelectedToUsers(users);
    } else if (type === "Notify") {
      setSelectedNotifyUsers(users);
    }
  };

  const handleRemoveUser = (id: string, type: string) => {
    if (type === "To") {
      setSelectedToUsers((prev) => prev.filter((user) => user.id !== id));
    } else if (type === "Notify") {
      setSelectedNotifyUsers((prev) => prev.filter((user) => user.id !== id));
    }
  };

  const clearFormData = () => {
    setTransmittalReferences({});
    setTransmittalSubject("");
    setTransmittalRemarks("");
    setSelectedToUsers([]);
    setSelectedNotifyUsers([]);
    setAlreadyAttachedResources([]);
    setReferencesDataset([]);
  };

  // Grid Templates, Column Definition and Tool Bar

  const referencesGridToolbar = [
    {
      name: t("Add"),
      id: "add",
      prefixIconTemplate: <CreateIcon />,
    },
    {
      name: t("Delete"),
      id: "delete",
      prefixIconTemplate: <DeleteIcon />,
      disabled: selectedRowCount === 0,
    },
  ];

  const nameTemplate = (_row, _cell, _value, _col, args) => {
    const container = document.createElement("div");
    container.style.width = "100%";
    const root = createRoot(container);
    const component = args.id ? (
      <>
        {args.name ? (
          <div className="flex items-center text-ellipsis overflow-hidden whitespace-nowrap px-2 py-4 gap-2">
            <img src={getFileImage(args)} className="w-5 h-5" />
            <span className="text-sm font-normal text-primary_text">{args.name}</span>
          </div>
        ) : null}
      </>
    ) : null;
    root.render(component);
    return container;
  };

  const versionTemplate = (_row, _cell, _value, _col, args) => {
    const container = document.createElement("div");
    container.style.width = "100%";
    const root = createRoot(container);
    const component = args.id ? (
      <>
        {args.versionNumber ? (
          <div className="flex items-center text-ellipsis overflow-hidden whitespace-nowrap px-2 py-4 gap-2">
            <span className="text-sm font-normal text-primary_text">V{args.versionNumber}</span>
          </div>
        ) : null}
      </>
    ) : null;
    root.render(component);
    return container;
  };

  const sizeTemplate = (_row, _cell, _value, _col, args) => {
    const container = document.createElement("div");
    container.style.width = "100%";
    const root = createRoot(container);
    const component = args.id ? (
      <>
        {args.size ? (
          <div className="flex items-center text-ellipsis overflow-hidden whitespace-nowrap px-2 py-4 gap-2">
            <span className="text-sm font-normal text-primary_text">{formatBytes(args.size)}</span>
          </div>
        ) : null}
      </>
    ) : null;
    root.render(component);
    return container;
  };

  const dateTemplate = (_row, _cell, _value, _col, args) => {
    const container = document.createElement("div");
    container.style.width = "100%";
    const root = createRoot(container);
    const component = args.id ? (
      <>
        {args.dateCreated ? (
          <div className="flex items-center text-ellipsis overflow-hidden whitespace-nowrap px-2 py-4 gap-2">
            <span className="text-sm font-normal text-primary_text">
              {convertDate(args.dateCreated, "dateForText")}
            </span>
          </div>
        ) : null}
      </>
    ) : null;
    root.render(component);
    return container;
  };

  const remarksTemplate = (_row, _cell, _value, _col, args) => {
    const container = document.createElement("div");
    container.style.width = "100%";
    const root = createRoot(container);

    const component = (
      <RemarksCell
        id={args.id}
        initialValue={transmittalReferences[args.id]?.remarks || ""}
        onValueChange={updateRemark}
      />
    );

    root.render(component);
    return container;
  };

  const referencesGridColumnDefinitions = useRef([
    {
      id: "name",
      field: "name",
      name: t("Name"),
      formatter: nameTemplate,
    },
    {
      id: "fileType",
      field: "fileType",
      name: t("File Type"),
    },
    {
      id: "versionNumber",
      field: "versionNumber",
      name: t("Version"),
      formatter: versionTemplate,
    },
    {
      id: "size",
      field: "size",
      name: t("Size"),
      formatter: sizeTemplate,
    },
    {
      id: "dateCreated",
      field: "dateCreated",
      name: t("Added On"),
      formatter: dateTemplate,
    },
    {
      id: "remarks",
      field: "remarks",
      name: t("Remarks"),
      formatter: remarksTemplate,
    },
  ]);

  // JSX Sections

  const modalHeader = () => (
    <div className="flex items-center justify-between w-full py-2 px-6 bg-surface_stroke_gradient_color1 rounded-t-lg">
      <p className="text-base font-bold">{t("Create Transmittal")}</p>
      <div className="flex items-center gap-4 ms-auto">
        <OriginSpaceDropDownList />
        <img src={Maximize} onClick={handleMaximize} alt="Maximize Modal" className="cursor-pointer" />
        <IconComponent name="close" onClick={onClose} className="cursor-pointer" />
      </div>
    </div>
  );

  const modalContent = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center items-center h-full w-full p-6">
          <CircularProgress size={40} />
        </div>
      );
    }

    if (spaces?.length < 2) {
      return (
        <div className="flex justify-center items-center">
          <NoDataSvg
            text1="A transmittal cannot be created in this project since there is only one space. A minimum of 2 spaces are required."
            height={300}
            width={300}
          />
        </div>
      );
    }

    return (
      <div className="flex bg-surface_white p-6 flex-col items-start gap-6 self-stretch">
        {/* To Users Section */}
        <div className="flex flex-col items-start p-4 gap-4 rounded-lg border border-surface_stroke_gradient_color1 w-full">
          <div className="flex gap-4 items-center">
            <div className="text-primary_text font-semibold text-base">{t("To")}</div>
            <ButtonComponent
              buttonType={BUTTON_TYPES.INFO_BACKGROUND_BUTTON}
              onClick={handleToClick}
              disabled={!toSpaceEnable}
            >
              <span className="flex flex-wrap items-center justify-start gap-2">
                <UsersIcon className="my-1.5 mx-0 rounded-2xl h-4 w-4" />
              </span>
            </ButtonComponent>
          </div>
          {selectedToUsers &&
            selectedToUsers.length > 0 &&
            Object.values(
              selectedToUsers.reduce((groups, user) => {
                if (!groups[user.spaceId]) {
                  groups[user.spaceId] = {
                    spaceId: user.spaceId,
                    spaceName: user.spaceName,
                    users: [],
                  };
                }
                groups[user.spaceId].users.push(user);
                return groups;
              }, {}),
            ).map((spaceGroup) => (
              <div className="flex gap-4 items-center" key={spaceGroup.spaceId}>
                <div className="text-primary_text font-semibold text-sm">{spaceGroup.spaceName}</div>
                <div className="flex flex-wrap gap-4">
                  {spaceGroup.users.map((user) => (
                    <UserNameTagComponent
                      key={user.id}
                      id={user.id.split("_")[0]}
                      handleDeleteIconClicked={() => handleRemoveUser(user.id, "To")}
                    />
                  ))}
                </div>
              </div>
            ))}
        </div>

        {/* Notify Users Section */}
        <div className="flex flex-col items-start p-4 gap-4 rounded-lg border border-surface_stroke_gradient_color1 w-full">
          <div className="flex gap-4 items-center">
            <div className="text-primary_text font-semibold text-base">{t("Notify")}</div>
            <ButtonComponent
              buttonType={BUTTON_TYPES.INFO_BACKGROUND_BUTTON}
              onClick={handleNotifyClick}
              disabled={!notifySpaceEnable}
            >
              <span className="flex flex-wrap items-center justify-start gap-2">
                <UsersIcon className="my-1.5 mx-0 rounded-2xl h-4 w-4" />
              </span>
            </ButtonComponent>
          </div>
          {selectedNotifyUsers &&
            selectedNotifyUsers.length > 0 &&
            Object.values(
              selectedNotifyUsers.reduce((groups, user) => {
                if (!groups[user.spaceId]) {
                  groups[user.spaceId] = {
                    spaceId: user.spaceId,
                    spaceName: user.spaceName,
                    users: [],
                  };
                }
                groups[user.spaceId].users.push(user);
                return groups;
              }, {}),
            ).map((spaceGroup) => (
              <div className="flex gap-4 items-center" key={spaceGroup.spaceId}>
                <div className="text-primary_text font-semibold text-sm">{spaceGroup.spaceName}</div>
                <div className="flex flex-wrap gap-4">
                  {spaceGroup.users.map((user) => (
                    <UserNameTagComponent
                      key={user.id}
                      id={user.id.split("_")[0]}
                      handleDeleteIconClicked={() => handleRemoveUser(user.id, "Notify")}
                    />
                  ))}
                </div>
              </div>
            ))}
        </div>

        {/* To Selector Component */}
        {isToSelectorOpen && (
          <SpaceUserSelector
            spaceList={filteredSpaceList}
            onSpaceSelectorModalClose={() => setIsToSelectorOpen(false)}
            onConfirm={(users: SelectedUsers) => handleSpaceUserSelection(users, "To")}
            selectedUsers={selectedToUsers}
          />
        )}

        {/* Notify Selector Component */}
        {isNotifySelectorOpen && (
          <SpaceUserSelector
            spaceList={filteredSpaceList}
            onSpaceSelectorModalClose={() => setIsNotifySelectorOpen(false)}
            onConfirm={(users: SelectedUsers) => handleSpaceUserSelection(users, "Notify")}
            selectedUsers={selectedNotifyUsers}
          />
        )}

        {/* Subject and Body Text Section */}
        <div className="flex flex-col items-start p-4 gap-4 rounded-lg border border-surface_stroke_gradient_color1 w-full overflow-x-hidden">
          <div className="flex items-center w-full">
            <div className="text-primary_text font-semibold text-base w-[100px] flex-shrink-0">{t("Subject")}</div>
            <input
              value={transmittalSubject}
              onChange={(e) => setTransmittalSubject(e.target.value)}
              className="e-input rounded-md flex-1"
              maxLength="500"
              placeholder={t("Enter Subject")}
            />
          </div>
          <div className="flex w-full">
            <div className="text-primary_text font-semibold text-base w-[100px] flex-shrink-0">{t("Body Text")}</div>
            <div className="w-full overflow-hidden">
              <RichTextEditorComponent
                ref={richTextEditorRef}
                key={`rte-${isOpen}`}
                showCharCount
                maxLength={255}
                toolbarSettings={editorToolbarSettings}
                placeholder={t("Type something")}
                height="150"
                width="100%"
                value={transmittalRemarks}
                change={handleTransmittalRemarksChange}
              >
                <Inject services={[Toolbar, HtmlEditor, Count]} />
              </RichTextEditorComponent>
            </div>
          </div>
        </div>

        {/* References Section */}
        {referencesModal()}

        {/* Custom Fields Section */}
        <div className="w-full">
          {formTemplate && (
            <FormPreview
              formValidator={formValidator}
              formData={formTemplate}
              disabled={false}
              handleData={handleCustomFieldsData}
            />
          )}
        </div>
      </div>
    );
  };

  const referencesModal = () => (
    <Accordion
      defaultExpanded
      disableGutters
      sx={{
        width: "100%",
        boxShadow: "none",
        "&:before": { display: "none" },
        border: "1px solid #ECF3FE",
        borderRadius: "8px",
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          padding: "16px",
          "& .MuiAccordionSummary-content": {
            margin: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          },
        }}
      >
        <div className="flex items-center gap-4">
          <div className="text-primary_text font-semibold text-base">{t("References")}</div>
          {Object.keys(transmittalReferences).length === 0 ? (
            <div onClick={(e) => e.stopPropagation()}>
              <ButtonComponent
                buttonType={BUTTON_TYPES.INFO_BACKGROUND_BUTTON}
                onClick={(e) => {
                  e.stopPropagation();
                  handleReferencesPopup();
                }}
              >
                <span className="flex flex-wrap items-center justify-start gap-2">
                  <AddIcon className="my-1.5 mx-0 rounded-2xl h-4 w-4" />
                </span>
              </ButtonComponent>
            </div>
          ) : null}
        </div>
      </AccordionSummary>
      <AccordionDetails sx={{ padding: "16px" }}>{getReferencesGrid()}</AccordionDetails>
    </Accordion>
  );

  const getReferencesGrid = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center items-center h-full w-full p-6">
          <CircularProgress size={40} />
        </div>
      );
    }

    return (
      <div className="overflow-auto h-full w-full">
        <div
          style={{
            opacity: referencesDataset.length > 0 ? 1 : 0,
            height: referencesDataset.length > 0 ? "30vh" : 0,
            overflow: "hidden",
          }}
        >
          <SlickGrid
            ref={referencesRef}
            columnDefinitions={referencesGridColumnDefinitions.current}
            dataset={referencesDataset}
            onClick={handleReferencesGridClick}
            toolbar={referencesGridToolbar}
            toolbarClickHandler={toolbarClickHandler}
            enableFiltering={false}
            onSelectedRowsChanged={handleSelectionChanged}
            gridOptions={{
              autosizeColumnsByCellContentOnFirstLoad: false,
              enableAutoResizeColumnsByCellContent: false,
              autoFitColumnsOnFirstLoad: true,
              enableAutoSizeColumns: true,
              forceFitColumns: true,
            }}
          />
        </div>
        {referencesDataset.length === 0 && <NoDataSvg text1={t("No references attached")} height={200} width={200} />}
      </div>
    );
  };

  const modalFooter = () => (
    <div className="flex w-full py-4 px-6 bg-surface_white border-t border-surface_stroke_gradient_color1 gap-4 rounded-b-lg">
      <ButtonComponent
        buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
        onClick={handleSendTransmittal}
        disabled={!enableSendTransmittal || spaces.length < 2 || isLoading}
        loading={buttonLoading}
      >
        {t("Send")}
      </ButtonComponent>
      <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={onClose}>
        {t("Cancel")}
      </ButtonComponent>
    </div>
  );

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      {isReferencesManagerOpen && (
        <FileManagerAsPopup
          projectId={projectId}
          handleFileSelectedButtonClick={handleReferencesSelectedButtonClick}
          handleFileSelectedCancelClick={handleReferencesPopupCancelClick}
          workspaceId={originSpace?._id}
          minimumFiles={1}
          isTransmittal
          hideSchedules
          hideTasks
          hideEstimates
          hideIssues
          hideReviews
          enableOnlyShareableResources={true}
          disableFiles={alreadyAttachedResources}
          enableFilesTypes={["RESOURCE_TYPE_FILE", "RESOURCE_TYPE_MERGED_FILE", "RESOURCE_TYPE_FOLDER"]}
          heading={`Attach References - ${originSpace?.spaceName}`}
          id="fileManagerAsPopup"
        />
      )}
      <div
        className={`bg-white rounded-sg shadow-lg flex flex-col transition-all duration-300 ease-in-out ${
          isMaximized ? "w-full h-full max-w-none" : "w-3/5 max-h-[90vh]"
        }`}
      >
        {modalHeader()}
        <div className="flex-1 overflow-y-auto">{modalContent()}</div>
        {modalFooter()}
      </div>
    </div>
  );
}

export default CreateTransmittal;
